import React, { useEffect, useState } from 'react';
import ReportDisplay from '../widgets/ReportDisplay';
import { TOTAL_ROW, cellText, dataRows, isNumericColumn, withTotalRow } from '../../utils/reportTable';
import { humanize } from './reportModel';
import { chartOptions } from '../../utils/cubeQuery';
import QueryProgress from '../widgets/QueryProgress';
import {
  IconTable, IconStat, IconBar, IconLine, IconArea, IconPie, IconPivot, IconCombo, IconRefresh, IconLink, IconWarn, IconTemplate,
  IconMeasure, IconText,
} from './icons';

export const DISPLAY_TYPES = [
  { id: 'table', name: 'Table', Icon: IconTable },
  { id: 'bar', name: 'Bar', Icon: IconBar },
  { id: 'line', name: 'Line', Icon: IconLine },
  { id: 'area', name: 'Area', Icon: IconArea },
  { id: 'combo', name: 'Bar + line', Icon: IconCombo },
  { id: 'pie', name: 'Pie', Icon: IconPie },
  { id: 'stat', name: 'Stat', Icon: IconStat },
  { id: 'pivot', name: 'Pivot', Icon: IconPivot },
];

const VIEWS = [['visual', 'Visual'], ['data', 'Data'], ['sql', 'SQL']];

/** The preview's rows as a plain grid, every column as returned, with the total row when there is one. */
const DataGrid = ({ table }) => {
  const shown = withTotalRow(table);
  return (
    <div className="rf-grid-scroll">
      <table className="rf-grid">
        <thead><tr>{shown.columns.map((c) => <th key={c.key} scope="col" title={c.error || undefined}>{c.label}{c.error ? ' ⚠' : ''}</th>)}</tr></thead>
        <tbody>
          {shown.rows.map((r, i) => (
            <tr key={i} className={r[TOTAL_ROW] ? 'rf-grid-total' : ''}>
              {shown.columns.map((c) => <td key={c.key} className={isNumericColumn(c) ? 'is-num' : ''}>{cellText(c, r[c.key])}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const SqlView = ({ loadSql, queryKey }) => {
  const [state, setState] = useState({ loading: true, sql: '', error: null });
  useEffect(() => {
    let live = true;
    setState({ loading: true, sql: '', error: null });
    loadSql()
      .then((sql) => live && setState({ loading: false, sql, error: null }))
      .catch((err) => live && setState({ loading: false, sql: '', error: err?.response?.data?.error || err?.message || 'Could not generate SQL' }));
    return () => { live = false; };
  }, [loadSql, queryKey]);
  if (state.loading) return <div className="rf-canvas-status">Generating SQL…</div>;
  if (state.error) return <div className="rf-canvas-status rf-canvas-status--error">{state.error}</div>;
  return <pre className="rf-sql"><code>{state.sql}</code></pre>;
};

/** Empty canvas: how to start, plus ready-made reports from the company's templates. */
const StartPanel = ({ library, onUseTemplate }) => (
  <div className="rf-start">
    <div className="rf-start-steps">
      <h3>Build a report</h3>
      <ol>
        <li><span className="rf-start-icon rf-field--measure"><IconMeasure size={14} /></span>Choose a measure from the Data panel, such as a count or a sum.</li>
        <li><span className="rf-start-icon rf-field--dimension"><IconText size={14} /></span>Add dimensions to split it into rows or categories.</li>
        <li><span className="rf-start-icon"><IconBar size={14} /></span>Pick how to display it. The preview updates as you go.</li>
      </ol>
      <p className="rf-muted">Click a field to add it, or drag it onto a shelf in the Build panel.</p>
    </div>
    {library.length > 0 && (
      <div className="rf-start-library">
        <h4><IconTemplate size={14} /> Start from a template report</h4>
        <div className="rf-start-cards">
          {library.slice(0, 9).map((r) => {
            const T = DISPLAY_TYPES.find((d) => d.id === r.displayType)?.Icon || IconTable;
            return (
              <button type="button" key={`${r.datasetName}:${r.key}`} className="rf-start-card" onClick={() => onUseTemplate(r)}>
                <span className="rf-start-card-icon"><T size={18} /></span>
                <span className="rf-start-card-text">
                  <strong>{r.title}</strong>
                  <small>{humanize(r.datasetName)}</small>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    )}
  </div>
);

/**
 * Centre pane: display type, the joins in use, and the live preview as a
 * dashboard widget, data grid or generated SQL.
 */
const PreviewCanvas = ({
  form, title, recommended, requirements, preview, previewLimit, onDisplayType, onRefresh,
  joins, catalogue, library, onUseTemplate, loadSql, queryKey, isEmpty, invalidReason,
}) => {
  const [view, setView] = useState('visual');
  const unmet = requirements.find((r) => !r.ok);
  const cubeTitle = (n) => catalogue.byCube.get(n)?.title || n;
  const table = preview.table;
  const rowCount = table ? dataRows(table).length : 0;
  const displayConfig = {
    displayType: form.displayType,
    target: form.target === '' ? null : Number(form.target),
    colorRules: form.colorRules,
    totalRow: form.totalRow,
    ...chartOptions(form),
    pivot: form.displayType === 'pivot' ? { rows: form.pivotRows, columns: form.pivotColumns, measure: form.pivotMeasure || form.measures[0] } : null,
  };
  const calcErrors = (table?.columns || []).filter((c) => c.error);
  // "Compare with" says plainly when it cannot show anything, rather than doing nothing.
  let compareProblem = null;
  if (form.compare) {
    if (form.measures.length === 0) compareProblem = 'needs a value to compare, such as a count or a sum. Add one under Values.';
    else if (!form.dateRange) compareProblem = 'needs a period. Choose one under Time. On a dashboard with a period set, that period is compared.';
    else if (form.displayType === 'pie') compareProblem = 'cannot be shown on a pie chart. Use a table, pivot, bar, line or stat to see it.';
  }

  let body;
  if (isEmpty) body = <StartPanel library={library} onUseTemplate={onUseTemplate} />;
  else if (invalidReason) body = <div className="rf-canvas-status rf-canvas-status--error"><IconWarn size={18} /> {invalidReason}</div>;
  else if (view === 'sql') body = <SqlView loadSql={loadSql} queryKey={queryKey} />;
  else if (preview.error) body = <div className="rf-canvas-status rf-canvas-status--error"><IconWarn size={18} /> {preview.error}</div>;
  else if (!table) body = <QueryProgress startedAt={preview.startedAt} retrying={preview.retrying} dark={false} />;
  else if (rowCount === 0) body = <div className="rf-canvas-status">The query returned no rows. Check the filters and period.</div>;
  else if (view === 'data') body = <DataGrid table={table} />;
  else {
    body = (
      <div className={`rf-widget-frame rf-widget-frame--${form.displayType}`}>
        <div className="widget report-widget rf-widget">
          <div className="widget-header"><span className="widget-title">{title}</span></div>
          <div className="widget-body report-body">
            <ReportDisplay table={table} config={displayConfig} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rf-canvas">
      <div className="rf-canvas-toolbar">
        <div className="rf-display-types" role="radiogroup" aria-label="Display type">
          {DISPLAY_TYPES.map(({ id, name, Icon }) => (
            <button
              key={id}
              type="button"
              role="radio"
              aria-checked={form.displayType === id}
              aria-label={name}
              title={recommended === id && form.displayType !== id ? `${name} (suggested for this selection)` : name}
              className={`rf-display-type ${form.displayType === id ? 'is-on' : ''} ${recommended === id ? 'is-recommended' : ''}`}
              onClick={() => onDisplayType(id)}
            >
              <Icon size={18} />
              <span>{name}</span>
            </button>
          ))}
        </div>
        <div className="rf-segmented rf-views" role="tablist" aria-label="Preview view">
          {VIEWS.map(([v, l]) => (
            <button key={v} type="button" role="tab" aria-selected={view === v} className={view === v ? 'is-on' : ''} onClick={() => setView(v)} disabled={isEmpty}>{l}</button>
          ))}
        </div>
      </div>

      {(unmet || joins.length > 0 || calcErrors.length > 0 || compareProblem) && !isEmpty && (
        <div className="rf-canvas-notes">
          {compareProblem && (
            <div className="rf-note rf-note--warn" role="status">
              <IconWarn size={14} />
              <span><strong>Compare with</strong> {compareProblem}</span>
            </div>
          )}
          {calcErrors.map((c) => (
            <div key={c.key} className="rf-note rf-note--warn" role="status">
              <IconWarn size={14} />
              <span><strong>{c.label}:</strong> {c.error}</span>
            </div>
          ))}
          {unmet && (
            <div className="rf-note rf-note--warn" role="status">
              <IconWarn size={14} />
              <span><strong>{DISPLAY_TYPES.find((d) => d.id === form.displayType)?.name} needs:</strong> {unmet.text.toLowerCase()}. {unmet.hint}.</span>
            </div>
          )}
          {joins.length > 0 && (
            <div className="rf-note" title={joins.map((j) => `${j.from}.${j.on}`).join('\n')}>
              <IconLink size={14} />
              <span>Combining {joins.map((j, i) => (
                <React.Fragment key={`${j.from}>${j.to}`}>
                  {i > 0 && ', '}<strong>{cubeTitle(j.from)}</strong> with <strong>{cubeTitle(j.to)}</strong> on <code>{j.on.split('=')[0].trim()}</code>
                </React.Fragment>
              ))}</span>
            </div>
          )}
        </div>
      )}

      <div className={`rf-canvas-body ${preview.loading && table ? 'is-refreshing' : ''}`}>{body}</div>

      {!isEmpty && !invalidReason && (
        <div className="rf-canvas-foot">
          <span>
            {preview.loading ? 'Running…'
              : table ? `${rowCount.toLocaleString()} row${rowCount === 1 ? '' : 's'}${preview.ms != null ? ` · ${preview.ms} ms` : ''}`
                : ''}
          </span>
          {table && !preview.loading && Number(form.limit) <= previewLimit && table.limitReached ? (
            <span className="rf-limit-warn"><IconWarn size={13} /> The results reached the row limit of {form.limit}. Raise it under Sort and limit to include every row.</span>
          ) : (
            <span className="rf-muted">Preview shows up to {previewLimit} rows. The saved report uses its row limit.</span>
          )}
          <button type="button" className="rf-icon-btn" onClick={onRefresh} aria-label="Refresh preview" title="Refresh preview" disabled={preview.loading}>
            <IconRefresh size={15} />
          </button>
        </div>
      )}
    </div>
  );
};

export default PreviewCanvas;
